import React, { useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import { calculateMaxDistance, getEVProfileName, fetchEVProfile } from '../../../utils/evProfiles';
import useEVData from './EV/useEVData';
import EVDropdowns from './EV/EVDropdowns';
import EVSpecifications from './EV/EVSpecifications';
import EVChargingInputs from './EVChargingInputs';


const StyledEVContainer = styled.div`
  background: rgba(34, 205, 200, 0.05);
  padding: 1rem;
  border-radius: 0.12rem;
  border: 1px solid rgba(34, 205, 200, 0.3);
  margin-bottom: 1rem;
`;

const EVSelector = ({
  oAuth,
  apiKey,
  onEVProfileChange,
  onAuthError,
  initialProfile,
  charging,
  vehicleAmount,
  onChargingChange,
}) => {
  const {
    makes,
    models,
    variants,
    selectedMake,
    selectedModel,
    selectedVariant,
    setSelectedMake,
    setSelectedModel,
    setSelectedVariant,
    selectedVariantData,
    authError,
  } = useEVData(oAuth, apiKey, initialProfile);

  const hasUserInteracted = useRef(!initialProfile);

  const onAuthErrorRef = useRef(onAuthError);

  useEffect(() => {
    onAuthErrorRef.current = onAuthError;
  }, [onAuthError]);

  useEffect(() => {
    if (authError && onAuthErrorRef.current) {
      onAuthErrorRef.current();
    }
  }, [authError]);

  const onEVProfileChangeRef = useRef(onEVProfileChange);

  useEffect(() => {
    onEVProfileChangeRef.current = onEVProfileChange;
  }, [onEVProfileChange]);

  useEffect(() => {
    if (selectedMake && selectedModel && selectedVariant && !isEmpty(variants)) {
      if (selectedVariantData) {
        hasUserInteracted.current = true;
        const makeName = makes.find((m) => m.id === selectedMake)?.name || selectedMake;
        const modelName = models.find((m) => m.id === selectedModel)?.name || selectedModel;
        const variantName =
          selectedVariantData.versions?.[0]?.label || selectedVariantData.profileId;
        const maxDistance = calculateMaxDistance(selectedVariantData.range);

        const loadProfile = async () => {
          const profileData = await fetchEVProfile(selectedVariantData.profileId, oAuth, apiKey);
          const batteryCapacity = profileData?.batteryCapacityKwh ?? null;
          onEVProfileChangeRef.current({
            profileId: selectedVariantData.profileId,
            maxDistance,
            make: makeName,
            model: modelName,
            variant: variantName,
            profileName: getEVProfileName(makeName, modelName, variantName),
            specs: {
              range: selectedVariantData.range,
              unladenWeight: selectedVariantData.unladenWeight,
              grossWeight: selectedVariantData.grossWeight,
              width: selectedVariantData.width,
              height: selectedVariantData.height,
              releaseYear: selectedVariantData.releaseYear,
              bodyType: selectedVariantData.bodyType,
              lengthClasses: selectedVariantData.lengthClasses,
              heightClasses: selectedVariantData.heightClasses,
              weightClasses: selectedVariantData.weightClasses,
              batteryCapacity,
            },
          });
        };
        loadProfile();
      }
    } else if (hasUserInteracted.current) {
      onEVProfileChangeRef.current(null);
    }
  }, [selectedMake, selectedModel, selectedVariant, variants, makes, models, selectedVariantData]);

  const handleMakeChange = useCallback(
    (e) => {
      setSelectedMake(e.target.value);
    },
    [setSelectedMake],
  );

  const handleModelChange = useCallback(
    (e) => {
      setSelectedModel(e.target.value);
    },
    [setSelectedModel],
  );

  const handleVariantChange = useCallback(
    (e) => {
      setSelectedVariant(e.target.value);
    },
    [setSelectedVariant],
  );

  return (
    <StyledEVContainer>
      <EVDropdowns
        makes={makes}
        models={models}
        variants={variants}
        selectedMake={selectedMake}
        selectedModel={selectedModel}
        selectedVariant={selectedVariant}
        onMakeChange={handleMakeChange}
        onModelChange={handleModelChange}
        onVariantChange={handleVariantChange}
      />
      <EVChargingInputs
        charging={charging}
        vehicleAmount={vehicleAmount}
        onChargingChange={onChargingChange}
      />
      <EVSpecifications variantData={selectedVariantData} />
    </StyledEVContainer>
  );
};

export default EVSelector;
